/**
 * Billing Service
 * Handles subscription, billing history, credits, and plans retrieval.
 * Integrates with backend /api/billing endpoints.
 */

import { BillingSettings, Invoice, SubscriptionPlan, SubscriptionStatus } from '../types/billing.types';
import { apiClient } from '../lib/api';

// The plans as shown to doctors: same prices and features as the landing's Pricing.tsx.
// Prices are shown in USD. Stripe charges the rounded CAD conversion, so change Stripe,
// this list and the landing together.
const PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Ideal for individual clinicians starting with AI transcription',
    priceMonthly: 0,
    priceYearly: 0,
    currency: 'USD',
    features: [
      'Unlimited sessions (free for now)',
      'Standard templates, plus 1 of your own',
      'Parallel Clinic with 1 intake script',
      'Ask Charti: 10 actions per month',
    ],
    isPopular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Perfect for growing practices',
    priceMonthly: 59,
    priceYearly: 588,
    currency: 'USD',
    features: [
      'Everything in Free',
      'Unlimited custom templates',
      'Parallel Clinic with unlimited intake scripts',
      'Ask Charti: unlimited actions',
      'Priority email support',
    ],
    isPopular: true,
  },
  {
    id: 'pair',
    name: 'Pair & Save',
    description: 'Two clinicians, one smart deal.',
    priceMonthly: 45,
    priceYearly: 540,
    currency: 'USD',
    features: [
      'All Pro plan benefits',
      'Starts once both clinicians have paid',
      'Lower yearly price per clinician',
      'Separate billing per user',
      'Seamless transition if one leaves',
    ],
    isPopular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Perfect for growing practices needing advanced features',
    priceMonthly: 0, // Contact Sales
    priceYearly: 0,
    currency: 'USD',
    features: [
      'All Pro plan benefits',
      'API and MCP access',
      'Hosted on your own cloud',
      'Usage reporting',
      'Doctors, clinics and assistants merged or split as you change',
      'Custom AI and EMR integration',
      'Volume pricing',
    ],
    isPopular: false,
  },
];

export interface CheckoutResponse {
    client_secret: string;
}

export interface PairValidationResponse {
    status: string;
}

/** The exact amounts for a doctor deciding not to wait for their partner, in cents. */
export interface PairOptions {
    currency: string;
    /** Null if Pro could not be priced just now. The refund is still available. */
    pro_today_amount: number | null;
    proration_date: number;
    refund_amount: number;
}

export interface CreditBalanceResponse {
    balance: number;
}

export const billingService = {
  getAvailablePlans: async (): Promise<SubscriptionPlan[]> => {
    return PLANS;
  },

  getBillingSettings: async (contextId?: string): Promise<BillingSettings> => {
      // Fetch subscription from backend
      try {
        const response = await apiClient.get<any>('/api/billing/subscription');
        const sub = response.data;
  
        // Determine status and plan from backend response
        // Default to free/inactive if no sub or if sub is not valid
        const planId = sub?.plan_id || 'free';
        const status = sub?.status || 'inactive';
        // Waiting means paid and active. A waiting plan that ended is simply Free, and a checkout
        // someone abandoned was never theirs.
        const isPairWaiting = planId === 'pair_pending' && status === 'active';
        const plan = isPairWaiting ? 'pair' : planId === 'pair_pending' || status === 'incomplete' ? 'free' : planId;
        
        // Map to BillingSettings
        return {
            plan,
            status: isPairWaiting ? 'pending_partner' : ((status as SubscriptionStatus) || 'inactive'),
            billingCycle: sub?.billing_cycle === 'yearly' ? 'yearly' : 'monthly',
            nextBillingDate: sub?.current_period_end ? new Date(sub.current_period_end) : undefined,
            paymentMethod: sub?.payment_method_details ? {
                type: 'card', // Only cards supported right now
                brand: sub.payment_method_details.brand,
                last4: sub.payment_method_details.last4,
                expiryMonth: sub.payment_method_details.exp_month,
                expiryYear: sub.payment_method_details.exp_year,
            } : undefined,
            usageLimits: { maxUsers: 1, maxStorage: 10 }, // Defaults
            currentUsage: { users: 1, storage: 0 },
            pairPartnerEmail: sub?.pair_partner_email ?? null,
            pairInvitedBy: sub?.pair_invited_by ?? null,
            pairPartnerAvailable: sub?.pair_partner_available ?? true,
            pairInviteResent: !!sub?.pair_invite_resent,
            pairRefundAvailableFrom: sub?.pair_refund_available_from ?? null,
        };
      } catch (error) {
        // If 401/403 or network error, return default free plan state
        // Or rethrow? Better to show default/empty state
        console.warn("Failed to fetch billing settings, defaulting to free", error);
        return {
            plan: 'free',
            status: (status as SubscriptionStatus) || 'inactive',
            billingCycle: 'monthly',
            usageLimits: { maxUsers: 1, maxStorage: 10 },
            currentUsage: { users: 1, storage: 0 },
        };
      }
  },
  
  getSubscription: async (): Promise<BillingSettings> => {
      return billingService.getBillingSettings();
  },

  getInvoices: async (): Promise<Invoice[]> => {
    try {
        const response = await apiClient.get<Invoice[]>('/api/billing/invoices');
        return response.data || [];
    } catch (error) {
        console.error("Failed to fetch invoices", error);
        return [];
    }
  },

  getCreditsBalance: async (): Promise<number> => {
    try {
        const response = await apiClient.get<CreditBalanceResponse>('/api/billing/credits/balance');
        return response.data.balance || 0;
    } catch (error) {
        console.error("Failed to fetch credit balance", error);
        return 0;
    }
  },
  
  purchaseCredits: async (packId: string): Promise<string> => {
      const baseUrl = (window as any).env?.VITE_APP_URL || import.meta.env.VITE_APP_URL;
      if (!baseUrl) {
          console.warn("VITE_APP_URL is not set in environment variables");
      }
      // Returns client_secret
      const response = await apiClient.post<CheckoutResponse>('/api/billing/credits/purchase', {
          pack_id: packId,
          success_url: `${baseUrl}/settings?tab=billing&success=true`,
          cancel_url: `${baseUrl}/settings?tab=billing&canceled=true`,
      });
      return response.data.client_secret;
  },

  upgradePlan: async (planId: string, billingCycle: 'monthly' | 'yearly'): Promise<void> => {
    // This is handled by createPaymentIntent/Checkout flow usually
    // Trigger checkout logic from UI
  },

  updatePaymentMethod: async (paymentMethodId: string): Promise<void> => {
    // Placeholder
  },

  createPortalSession: async (returnUrl: string): Promise<string> => {
      const response = await apiClient.post<{url: string}>('/api/billing/portal', {
          return_url: returnUrl
      });
      return response.data.url;
  },

  createPaymentIntent: async (planId: string, billingCycle: 'monthly' | 'yearly', partnerEmail?: string): Promise<string> => {
      const baseUrl = (window as any).env?.VITE_APP_URL || import.meta.env.VITE_APP_URL || window.location.origin;
      if (!(window as any).env?.VITE_APP_URL && !import.meta.env.VITE_APP_URL) {
          console.warn("VITE_APP_URL is not set in environment variables, using window.location.origin instead");
      }
      const response = await apiClient.post<CheckoutResponse>('/api/billing/checkout', {
          plan_id: planId,
          billing_cycle: billingCycle,
          partner_email: partnerEmail,
          success_url: `${baseUrl}/settings?tab=billing&success=true`,
          cancel_url: `${baseUrl}/settings?tab=billing&canceled=true`,
      });
      return response.data.client_secret;
  },
  
  validatePairPartner: async (partnerEmail: string): Promise<PairValidationResponse> => {
      const response = await apiClient.post<PairValidationResponse>('/api/billing/pair/validate', {
          partner_email: partnerEmail
      });
      return response.data;
  },

  /** Send the invitation again, or name someone else. "linked" means they were already waiting. */
  changePairPartner: async (partnerEmail: string): Promise<'invited' | 'linked'> => {
      const response = await apiClient.post<{ status: 'invited' | 'linked' }>('/api/billing/pair/partner', {
          partner_email: partnerEmail
      });
      return response.data.status;
  },

  getPairOptions: async (): Promise<PairOptions> => {
      const response = await apiClient.get<PairOptions>('/api/billing/pair/options');
      return response.data;
  },

  startProToday: async (prorationDate: number): Promise<void> => {
      await apiClient.post('/api/billing/pair/start-pro', { proration_date: prorationDate });
  },

  refundWaitingPair: async (): Promise<void> => {
      await apiClient.post('/api/billing/pair/refund');
  },

  /** From the link in the invitation email. Needs no account: the signed token authorizes it. */
  optOutOfPairInvites: async (token: string): Promise<void> => {
      await apiClient.post('/api/billing/pair/opt-out', { token });
  }
};
